import React from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";
const GRN = "#1D9E75";
const CARD = "#FFFFFF";
const B = "#E1E9EE";

const SectionLabel = ({ children }) => (
  <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, letterSpacing: "0.2em", color: "#999999", textTransform: "uppercase", marginBottom: 16 }}>{children}</div>
);
const Divider = () => <div style={{ height: 1, background: B, margin: "28px 0" }} />;

const PROFESSIONS = [
  { id: "general", label: "General", desc: "Standard content safety checks for all professionals." },
  { id: "financial_advisor", label: "Financial Advisor", desc: "FINRA 2210 rules: no guarantees, balanced risk, disclosure requirements." },
  { id: "lawyer", label: "Lawyer", desc: "No outcome guarantees, solicitation rules, confidentiality." },
  { id: "accountant", label: "Accountant", desc: "Tax advice framing, confidentiality, independence implications." },
];

function Toggle({ value, onToggle, label, desc, color }) {
  const bg = color || GRN;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "14px 18px", marginBottom: 14 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, marginBottom: 4 }}>{label}</div>
        <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666666" }}>{desc}</div>
      </div>
      <button onClick={onToggle} style={{ background: value ? bg : "#E0E0E0", color: value ? "#fff" : "#666", border: "none", borderRadius: 20, padding: "6px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: "pointer", minWidth: 56, transition: "all 0.2s" }}>
        {value ? "On" : "Off"}
      </button>
    </div>
  );
}

export default function ComplianceTab({ user, setUser }) {
  const saveSetting = async (key, val) => {
    await fetch(`${API_BASE}/app/api/user/settings`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ [key]: val }) });
    setUser(u => u ? { ...u, [key]: val } : u);
  };

  const checkerOn = user?.compliance_checker_enabled !== false;
  const profession = user?.compliance_profession || "general";

  return (
    <div>
      <SectionLabel>My profession</SectionLabel>
      <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 16, lineHeight: 1.6 }}>
        Compliance rules are tailored to your profession's regulatory requirements.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 28 }}>
        {PROFESSIONS.map(({ id, label, desc }) => {
          const active = profession === id;
          return (
            <div key={id} onClick={() => saveSetting("compliance_profession", id)}
              style={{ border: `1px solid ${active ? G : B}`, borderRadius: 10, padding: "16px 14px", cursor: "pointer", background: active ? "#EBF5FB" : "transparent", transition: "all 0.2s" }}>
              <div style={{ fontSize: 15, marginBottom: 6 }}>{label}{active && <span style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 10, color: G, marginLeft: 6 }}>✓ active</span>}</div>
              <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666666", lineHeight: 1.6 }}>{desc}</div>
            </div>
          );
        })}
      </div>

      <Divider />
      <SectionLabel>Compliance checker</SectionLabel>
      <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 20, lineHeight: 1.6 }}>
        BrandHives reviews every post before it goes out to flag anything that could create professional or regulatory risk.
      </div>

      <Toggle value={checkerOn} onToggle={() => saveSetting("compliance_checker_enabled", !checkerOn)}
        label="Check all posts before posting"
        desc={checkerOn ? "Every post is reviewed against compliance rules before it goes out." : "Posts go out without compliance review. Use with caution."} />

      {checkerOn && (
        <>
          {/* Auto-fix toggle hidden — feature not ready for launch */}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "14px 18px", marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 15 }}>Strict mode</span>
                <span style={{ fontSize: 10, background: "#FEF3C7", color: "#92400E", borderRadius: 4, padding: "2px 7px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: 700 }}>EXTRA CAUTIOUS</span>
              </div>
              <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666666" }}>
                {user?.compliance_block_on_warn ? "Warnings are treated as blocks — posts with any flag are held until reviewed." : "Only hard compliance violations block a post. Warnings are logged but posts still go out."}
              </div>
            </div>
            <button onClick={() => saveSetting("compliance_block_on_warn", !user?.compliance_block_on_warn)}
              style={{ background: user?.compliance_block_on_warn ? "#F59E0B" : "#E0E0E0", color: user?.compliance_block_on_warn ? "#fff" : "#666", border: "none", borderRadius: 20, padding: "6px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, cursor: "pointer", minWidth: 56, transition: "all 0.2s" }}>
              {user?.compliance_block_on_warn ? "On" : "Off"}
            </button>
          </div>

          {/* compliance_notify_email toggle hidden — backend logic retained */}
        </>
      )}

      <div style={{ background: "#F9FAFB", border: `1px solid ${B}`, borderRadius: 10, padding: "14px 18px", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 10, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>What we check for</div>
        <div style={{ fontSize: 12, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", lineHeight: 1.8 }}>
          {profession === "financial_advisor" && <div>· Performance guarantees (FINRA 2210) · Promissory language · Unbalanced risk presentation · Testimonials · Missing disclosures</div>}
          {profession === "lawyer" && <div>· Outcome guarantees · Direct solicitation rules · Client confidentiality · Jurisdiction disclaimers</div>}
          {profession === "accountant" && <div>· Tax advice framing · Client confidentiality · Independence implications</div>}
          <div>· Invented statistics · Unverifiable claims · Fabricated endorsements · Absolute language · General disclaimer recommendations</div>
        </div>
      </div>

      <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "14px 18px", marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", lineHeight: 1.7 }}>
          <strong>Compliance Assist</strong> helps flag common regulatory language patterns before your posts go out. It is not a substitute for professional compliance review and does not guarantee regulatory compliance. Always consult your compliance officer or regulator. BrandHives is not responsible for regulatory outcomes.
        </div>
      </div>

      <Divider />
      <SectionLabel>Content safety</SectionLabel>
      <div style={{ fontSize: 14, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 20, lineHeight: 1.6 }}>
        Control how BrandHives handles sensitive content in your post feed.
      </div>

      <Toggle value={user?.news_sensitive_topics} onToggle={() => saveSetting("news_sensitive_topics", !user?.news_sensitive_topics)}
        label="Sensitive content filter"
        desc="By default BrandHives excludes tragedy, disaster, and crisis news. Turn this on to include sensitive topics in your feed." />

      <Toggle value={user?.generation_preview} onToggle={() => saveSetting("generation_preview", !user?.generation_preview)}
        label="Preview posts before they go out"
        desc={user?.generation_preview ? "Posts are held in preview status until you approve them." : "Posts go out automatically. Off by default."} />
    </div>
  );
}
